import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPrint } from 'react-icons/fa';
import MainScreenHeader from '../../../common/Header/MainScreenHeader';
import Toolbar, { ToolbarButtonConfig, ToolbarFilterConfig } from '../../../common/Toolbar/Toolbar';
import ErrorMessage from '../../../common/ErrorMessage/ErrorMessage';
import StatsBar from '../../../common/StatsBar/StatsBar';
import PrintableView, { PrintablePage } from '../../../common/PrintableView/PrintableView';
import { FiscalYearRestDataProvider } from '../../../../data/FiscalYearRestDataProvider';
import { LedgerRestDataProvider } from '../../../../data/LedgerRestDataProvider';
import { LedgerEntryRestDataProvider } from '../../../../data/LedgerEntryRestDataProvider';
import { CompanyInfoRestDataProvider } from '../../../../data/CompanyInfoRestDataProvider';
import { FixedAssetRestDataProvider } from '../../../../data/FixedAssetRestDataProvider';
import { DepreciationPeriodRestDataProvider } from '../../../../data/DepreciationPeriodRestDataProvider';
import { FiscalYear, FiscalYearState } from '../../../../models/FiscalYear';
import { Ledger } from '../../../../models/Ledger';
import { LedgerEntry } from '../../../../models/LedgerEntry';
import { CompanyInfo } from '../../../../models/CompanyInfo';
import { FixedAsset } from '../../../../models/FixedAsset';
import { DepreciationPeriod } from '../../../../models/DepreciationPeriod';
import { LedgerType } from '../../../../models/LedgerType';
import './AnnualStatement.scss';

// ── Types ─────────────────────────────────────────────────────────────────────

interface LedgerBalance {
    ledger: Ledger;
    balance: number;
    /** True for accumulated-depreciation contra-assets (codes 0200–0299). */
    isContraAsset: boolean;
}

// ── Pure helpers ──────────────────────────────────────────────────────────────

/**
 * Accounts in the 02xx code range are accumulated-depreciation contra-assets.
 * They are typed `Debet` in the chart-of-accounts but always carry a net credit
 * balance, so their computed balance is negative (debitAmount - creditAmount < 0).
 * e.g. code "0201" → parseInt = 201, which falls in 200–299.
 */
function isContraAssetLedger(ledger: Ledger): boolean {
    const n = parseInt(ledger.code, 10);
    return !isNaN(n) && n >= 200 && n <= 299;
}

/**
 * Compute the closing balance for one ledger.
 *
 * Balance-sheet accounts (Debet / Credit) use the CUMULATIVE entry set
 * (all entries with entryMoment ≤ fiscal-year end) so that amounts from
 * prior years are included.
 *
 * P&L accounts (Costs / Proceeds) use only the entries that belong to the
 * selected fiscal year.
 */
function computeBalance(
    ledger: Ledger,
    bsEntries: LedgerEntry[],
    plEntries: LedgerEntry[],
): LedgerBalance {
    const isBS = ledger.type === LedgerType.Debet || ledger.type === LedgerType.Credit;
    const relevant = isBS ? bsEntries : plEntries;
    const filtered = relevant.filter(e => e.ledgerId === ledger.id);
    const totalDebet  = filtered.reduce((s, e) => s + (e.debetAmount  ?? 0), 0);
    const totalCredit = filtered.reduce((s, e) => s + (e.creditAmount ?? 0), 0);
    let balance: number;
    switch (ledger.type) {
        case LedgerType.Debet:    balance = totalDebet  - totalCredit; break;
        case LedgerType.Credit:   balance = totalCredit - totalDebet;  break;
        case LedgerType.Costs:    balance = totalDebet  - totalCredit; break;
        case LedgerType.Proceeds: balance = totalCredit - totalDebet;  break;
        default:                  balance = totalDebet  - totalCredit;
    }
    return { ledger, balance, isContraAsset: isContraAssetLedger(ledger) };
}

function groupBalances(balances: LedgerBalance[]): Map<string, LedgerBalance[]> {
    const map = new Map<string, LedgerBalance[]>();
    const sorted = [...balances].sort((a, b) => a.ledger.code.localeCompare(b.ledger.code));
    for (const lb of sorted) {
        const key = lb.ledger.group?.trim() || 'Overig';
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(lb);
    }
    return map;
}

// ── Component ─────────────────────────────────────────────────────────────────

const AnnualStatement: React.FC = () => {
    const { t } = useTranslation(['common', 'screens']);

    const [initialized,          setInitialized]          = useState(false);
    const [loading,              setLoading]              = useState(false);
    const [error,                setError]                = useState<string | null>(null);
    const [fiscalYears,          setFiscalYears]          = useState<FiscalYear[]>([]);
    const [selectedFiscalYearId, setSelectedFiscalYearId] = useState('');
    const [ledgers,              setLedgers]              = useState<Ledger[]>([]);
    const [allEntries,           setAllEntries]           = useState<LedgerEntry[]>([]);
    const [companyInfo,          setCompanyInfo]          = useState<CompanyInfo | null>(null);
    const [fixedAssets,          setFixedAssets]          = useState<FixedAsset[]>([]);
    const [depreciationPeriods,  setDepreciationPeriods]  = useState<DepreciationPeriod[]>([]);
    const [showPrint,            setShowPrint]            = useState(false);

    const fiscalYearProvider   = useMemo(() => new FiscalYearRestDataProvider(),          []);
    const ledgerProvider       = useMemo(() => new LedgerRestDataProvider(),              []);
    const entryProvider        = useMemo(() => new LedgerEntryRestDataProvider(),         []);
    const companyInfoProvider  = useMemo(() => new CompanyInfoRestDataProvider(),         []);
    const fixedAssetProvider   = useMemo(() => new FixedAssetRestDataProvider(),          []);
    const depreciationProvider = useMemo(() => new DepreciationPeriodRestDataProvider(),  []);

    // ── Phase 1: reference data ───────────────────────────────────────────────

    useEffect(() => {
        Promise.all([
            fiscalYearProvider.getAll(),
            ledgerProvider.getAll(),
            companyInfoProvider.getInfo().catch(() => null),
            depreciationProvider.getAll(),
        ])
        .then(([years, leds, info, periods]) => {
            const sorted = [...years].sort(
                (a, b) => new Date(b.start).getTime() - new Date(a.start).getTime()
            );
            setFiscalYears(sorted);
            if (sorted.length > 0) setSelectedFiscalYearId(sorted[0].id);
            setLedgers(leds);
            setCompanyInfo(info);
            setDepreciationPeriods(periods);
            setInitialized(true);
        })
        .catch(e => setError((e as Error).message));
    }, [fiscalYearProvider, ledgerProvider, companyInfoProvider, depreciationProvider]);

    // ── Phase 2: ALL ledger entries + fixed assets ────────────────────────────

    const loadData = useCallback(async () => {
        if (!selectedFiscalYearId) return;
        setLoading(true);
        setError(null);
        try {
            const [all, assets] = await Promise.all([
                entryProvider.getAll(),
                fixedAssetProvider.getAll(),
            ]);
            setAllEntries(all);
            setFixedAssets(assets);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setLoading(false);
        }
    }, [entryProvider, fixedAssetProvider, selectedFiscalYearId]);

    useEffect(() => {
        if (initialized) loadData();
    }, [loadData, initialized]);

    // ── Derived entry sets ────────────────────────────────────────────────────

    const selectedFiscalYear = useMemo(
        () => fiscalYears.find(fy => fy.id === selectedFiscalYearId) ?? null,
        [fiscalYears, selectedFiscalYearId]
    );

    /**
     * Balance-sheet entry set: ALL entries with entryMoment ≤ fiscal-year end.
     * Produces the CUMULATIVE closing balance rather than only current-year movements.
     */
    const bsEntries = useMemo(() => {
        if (!selectedFiscalYear) return [];
        const yearEnd = selectedFiscalYear.end.substring(0, 10); // "yyyy-mm-dd"
        return allEntries.filter(e => e.entryMoment.substring(0, 10) <= yearEnd);
    }, [allEntries, selectedFiscalYear]);

    /**
     * P&L entry set: entries belonging to the selected fiscal year, EXCLUDING
     * year-end closing entries (bookingReference starting with 'CLOSE').
     *
     * Closing entries (afsluitboekingen) zero out P&L accounts when a year is
     * closed.  Excluding them here lets us display the actual revenues and costs
     * for the year even after the year has been closed.
     */
    const plEntries = useMemo(
        () => allEntries.filter(
            e => e.fiscalYearId === selectedFiscalYearId &&
                 !e.bookingReference?.toUpperCase().startsWith('CLOSE')
        ),
        [allEntries, selectedFiscalYearId]
    );

    /** True when the selected fiscal year has been formally closed (afsluitboekingen posted). */
    const isYearClosed = selectedFiscalYear?.state === FiscalYearState.Closed;

    // ── Derived balances ──────────────────────────────────────────────────────

    const allBalances = useMemo(
        () => ledgers
            .map(l => computeBalance(l, bsEntries, plEntries))
            .filter(lb => Math.abs(lb.balance) >= 0.005),
        [ledgers, bsEntries, plEntries]
    );

    const activaBalances      = useMemo(() => allBalances.filter(lb => lb.ledger.type === LedgerType.Debet),    [allBalances]);
    const passivaBalances     = useMemo(() => allBalances.filter(lb => lb.ledger.type === LedgerType.Credit),   [allBalances]);
    const opbrengstenBalances = useMemo(() => allBalances.filter(lb => lb.ledger.type === LedgerType.Proceeds), [allBalances]);
    const kostenBalances      = useMemo(() => allBalances.filter(lb => lb.ledger.type === LedgerType.Costs),    [allBalances]);

    // Contra-asset balances are negative, so they naturally reduce totalActiva.
    const totalActiva      = useMemo(() => activaBalances.reduce((s, lb)      => s + lb.balance, 0), [activaBalances]);
    const totalPassiva     = useMemo(() => passivaBalances.reduce((s, lb)     => s + lb.balance, 0), [passivaBalances]);
    const totalOpbrengsten = useMemo(() => opbrengstenBalances.reduce((s, lb) => s + lb.balance, 0), [opbrengstenBalances]);
    const totalKosten      = useMemo(() => kostenBalances.reduce((s, lb)      => s + lb.balance, 0), [kostenBalances]);
    const nettoresultaat   = totalOpbrengsten - totalKosten;

    /**
     * ALL-TIME cumulative net P&L result: P&L accounts are evaluated against
     * bsEntries (all entries ≤ year-end) rather than the current-year-only
     * plEntries.  This mirrors the cumulative window already used for balance-
     * sheet accounts and is the quantity needed to satisfy:
     *
     *   Activa = Passiva + cumulativeNetResult
     *
     * When year-end closing entries (afsluitboekingen) exist, they debit the P&L
     * accounts, reducing their balance to zero so cumulativeNetResult collapses to
     * nettoresultaat (current year) and no extra passiva line appears.
     *
     * Without closing entries, the delta (priorYearAccumulatedResult) represents
     * accumulated prior-year profits/losses not yet transferred to Eigen Vermogen,
     * which we surface as a separate passiva line so the balance sheet still closes.
     */
    const cumulativeNetResult = useMemo(
        () => ledgers
            .filter(l => l.type === LedgerType.Proceeds || l.type === LedgerType.Costs)
            .map(l => computeBalance(l, bsEntries, bsEntries)) // use cumulative set for all-time P&L
            .reduce((s, lb) => {
                if (lb.ledger.type === LedgerType.Proceeds) return s + lb.balance;
                return s - lb.balance; // Costs reduce the net result
            }, 0),
        [ledgers, bsEntries]
    );

    /** Portion of cumulativeNetResult that belongs to prior fiscal years (≠ 0 when closing entries are absent). */
    const priorYearAccumulatedResult = cumulativeNetResult - nettoresultaat;

    /**
     * Dutch GAAP (BW2) requires Activa = Passiva + (all accumulated P&L).
     * With the cumulativeNetResult the equation holds even without year-end
     * closing entries, so a non-zero diff now always indicates actual booking errors.
     */
    const balanceDiff = useMemo(
        () => Math.abs(totalActiva - (totalPassiva + cumulativeNetResult)),
        [totalActiva, totalPassiva, cumulativeNetResult]
    );
    const isBalanced = balanceDiff < 0.01;

    const activaGroups      = useMemo(() => groupBalances(activaBalances),      [activaBalances]);
    const passivaGroups     = useMemo(() => groupBalances(passivaBalances),     [passivaBalances]);
    const opbrengstenGroups = useMemo(() => groupBalances(opbrengstenBalances), [opbrengstenBalances]);
    const kostenGroups      = useMemo(() => groupBalances(kostenBalances),      [kostenBalances]);

    // ── Vermogensmutatiestaat (Statement of Changes in Equity) ────────────────

    /** Resolve the three capital/prive ledgers by code. */
    const eigenVermogenLedger = useMemo(() => ledgers.find(l => l.code === '0700') ?? null, [ledgers]);
    const priveOpnamesLedger  = useMemo(() => ledgers.find(l => l.code === '0702') ?? null, [ledgers]);
    const priveStortingLedger = useMemo(() => ledgers.find(l => l.code === '0701') ?? null, [ledgers]);

    /**
     * Opening Eigen Vermogen: balance of 0700 from ALL entries BEFORE this
     * fiscal year's start date (i.e. prior-year cumulative EV).
     */
    const openingEigenVermogen = useMemo(() => {
        if (!eigenVermogenLedger || !selectedFiscalYear) return 0;
        const fyStart = selectedFiscalYear.start.substring(0, 10);
        return allEntries
            .filter(e => e.ledgerId === eigenVermogenLedger.id &&
                         e.entryMoment.substring(0, 10) < fyStart)
            .reduce((s, e) => s + ((e.creditAmount ?? 0) - (e.debetAmount ?? 0)), 0);
    }, [allEntries, eigenVermogenLedger, selectedFiscalYear]);

    /**
     * This year's prive opnames (0702) — net debit from plEntries (CLOSE excluded).
     * Positive = owner has withdrawn money.
     */
    const priveOpnamesThisYear = useMemo(() => {
        if (!priveOpnamesLedger) return 0;
        return plEntries
            .filter(e => e.ledgerId === priveOpnamesLedger.id)
            .reduce((s, e) => s + ((e.debetAmount ?? 0) - (e.creditAmount ?? 0)), 0);
    }, [plEntries, priveOpnamesLedger]);

    /**
     * This year's prive stortingen (0701) — net credit from plEntries (CLOSE excluded).
     * Positive = owner has deposited money.
     */
    const priveStortingThisYear = useMemo(() => {
        if (!priveStortingLedger) return 0;
        return plEntries
            .filter(e => e.ledgerId === priveStortingLedger.id)
            .reduce((s, e) => s + ((e.creditAmount ?? 0) - (e.debetAmount ?? 0)), 0);
    }, [plEntries, priveStortingLedger]);

    /**
     * Projected / actual closing Eigen Vermogen.
     * For a closed year this will equal the 0700 balance on the balance sheet.
     * For an open year it shows what EV will be after year-end closing.
     */
    const closingEigenVermogen =
        openingEigenVermogen + nettoresultaat - priveOpnamesThisYear + priveStortingThisYear;

    // ── Fixed-asset helpers ───────────────────────────────────────────────────

    const bookedAssets = useMemo(() => {
        const yearEnd = selectedFiscalYear ? selectedFiscalYear.end.substring(0, 10) : null;
        return fixedAssets.filter(a =>
            a.isBooked &&
            (!yearEnd || a.dateOfPurchase.substring(0, 10) <= yearEnd)
        );
    }, [fixedAssets, selectedFiscalYear]);

    // ── Formatting helpers ────────────────────────────────────────────────────

    const fmt = (v: number) =>
        v.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const fmtEuro = (v: number) => `€\u00a0${fmt(v)}`;

    const fmtDate = (dateStr: string) =>
        new Date(dateStr).toLocaleDateString('nl-NL', {
            day: 'numeric', month: 'long', year: 'numeric',
        });

    const fmtDateShort = (dateStr: string) =>
        new Date(dateStr).toLocaleDateString('nl-NL', {
            day: '2-digit', month: '2-digit', year: 'numeric',
        });

    const hasData = allBalances.length > 0;

    // ── Screen render helpers ─────────────────────────────────────────────────

    /**
     * Renders balance rows for one group.
     *
     * Gross activa (01xx) are shown normally.
     * Contra-assets (02xx, accumulated depreciation) appear as indented deduction
     * rows with the amount in parentheses — standard Dutch GAAP presentation.
     * When a group contains both, a "Boekwaarde" net subtotal is appended.
     */
    const renderGroups = (groups: Map<string, LedgerBalance[]>) =>
        Array.from(groups.entries()).map(([groupName, bals]) => {
            const grossBals  = bals.filter(lb => !lb.isContraAsset);
            const contraBals = bals.filter(lb => lb.isContraAsset);
            const groupTotal = bals.reduce((s, lb) => s + lb.balance, 0);
            const hasContra  = contraBals.length > 0;
            return (
                <React.Fragment key={groupName}>
                    <div className="as-group-title">{groupName}</div>
                    {grossBals.map(lb => (
                        <div key={lb.ledger.id} className="as-ledger-row">
                            <span className="as-ledger-code">{lb.ledger.code}</span>
                            <span className="as-ledger-name">{lb.ledger.name}</span>
                            <span className="as-ledger-amount">{fmt(lb.balance)}</span>
                        </div>
                    ))}
                    {contraBals.map(lb => (
                        <div key={lb.ledger.id} className="as-ledger-row as-ledger-row--contra">
                            <span className="as-ledger-code">{lb.ledger.code}</span>
                            <span className="as-ledger-name as-contra-label">
                                {t('screens:annualStatement.afMin')}&nbsp;{lb.ledger.name}
                            </span>
                            <span className="as-ledger-amount as-contra-amount">
                                ({fmt(Math.abs(lb.balance))})
                            </span>
                        </div>
                    ))}
                    {hasContra && (
                        <div className="as-net-book-value-row">
                            <span>{t('screens:annualStatement.boekwaarde')}</span>
                            <span>{fmt(groupTotal)}</span>
                        </div>
                    )}
                    {!hasContra && groups.size > 1 && (
                        <div className="as-group-subtotal">
                            <span>{groupName}</span>
                            <span>{fmt(groupTotal)}</span>
                        </div>
                    )}
                </React.Fragment>
            );
        });

    // ── Vermogensmutatiestaat render helpers ──────────────────────────────────

    /** Shared row renderer used by both the screen and print vermogensmutatiestaat. */
    const renderVermogensmutatieRows = (rowClass: string, totalClass: string) => (
        <>
            <div className={rowClass}>
                <span>{t('screens:annualStatement.vmsOpening', {
                    date: selectedFiscalYear ? fmtDate(selectedFiscalYear.start) : '',
                })}</span>
                <span>{fmt(openingEigenVermogen)}</span>
            </div>
            <div className={rowClass}>
                <span>{nettoresultaat >= 0
                    ? `+ ${t('screens:annualStatement.nettoresultaat')} (${t('screens:annualStatement.profit')})`
                    : `− ${t('screens:annualStatement.nettoresultaat')} (${t('screens:annualStatement.loss')})`}
                </span>
                <span className={nettoresultaat >= 0 ? 'as-positive' : 'as-negative'}>
                    {nettoresultaat < 0
                        ? `(${fmt(Math.abs(nettoresultaat))})`
                        : fmt(nettoresultaat)}
                </span>
            </div>
            {priveOpnamesThisYear >= 0.005 && (
                <div className={rowClass}>
                    <span>− {t('screens:annualStatement.vmsPriveOpnames')}</span>
                    <span className="as-negative">({fmt(priveOpnamesThisYear)})</span>
                </div>
            )}
            {priveStortingThisYear >= 0.005 && (
                <div className={rowClass}>
                    <span>+ {t('screens:annualStatement.vmsPriveStortingen')}</span>
                    <span className="as-positive">{fmt(priveStortingThisYear)}</span>
                </div>
            )}
            <div className={totalClass}>
                <span>{t('screens:annualStatement.vmsClosing', {
                    date: selectedFiscalYear ? fmtDate(selectedFiscalYear.end) : '',
                })}</span>
                <span>{fmt(closingEigenVermogen)}</span>
            </div>
        </>
    );

    // ── Toelichting Materiële Vaste Activa ────────────────────────────────────

    const renderMvaToelichting = () => {
        if (bookedAssets.length === 0) return null;

        const totalPrice         = bookedAssets.reduce((s, a) => s + a.price, 0);
        const totalDeprPerYear   = bookedAssets.reduce((s, a) => s + a.depreciationPerYear, 0);
        const totalDeprAmount    = bookedAssets.reduce((s, a) => s + a.depreciationAmount, 0);
        const totalRemainingValue = bookedAssets.reduce((s, a) => s + a.remainingValue, 0);

        return (
            <div className="annual-statement-section">
                <div className="as-section-header">
                    <span className="as-section-title">
                        {t('screens:annualStatement.mvaToelichting')}
                    </span>
                    {selectedFiscalYear && (
                        <span className="as-section-period">
                            {t('screens:annualStatement.wplPeriod', { period: selectedFiscalYear.name })}
                        </span>
                    )}
                </div>
                <div className="as-mva-table-wrapper">
                    <table className="as-mva-table">
                        <thead>
                            <tr>
                                <th className="as-mva-th">{t('screens:annualStatement.mvaName')}</th>
                                <th className="as-mva-th">{t('screens:annualStatement.mvaPurchaseDate')}</th>
                                <th className="as-mva-th as-mva-num">{t('screens:annualStatement.mvaPurchasePrice')}</th>
                                <th className="as-mva-th as-mva-num">{t('screens:annualStatement.mvaBusinessUse')}</th>
                                <th className="as-mva-th as-mva-num">{t('screens:annualStatement.mvaDepreciationPeriod')}</th>
                                <th className="as-mva-th as-mva-num">{t('screens:annualStatement.mvaDepreciationPerYear')}</th>
                                <th className="as-mva-th as-mva-num">{t('screens:annualStatement.mvaDepreciatedToDate')}</th>
                                <th className="as-mva-th as-mva-num">{t('screens:annualStatement.mvaBoekwaarde')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bookedAssets.map(asset => {
                                const period = depreciationPeriods.find(p => p.id === asset.depreciationPeriodId);
                                return (
                                    <tr key={asset.id} className={asset.isCompletelyDepreciated ? 'as-mva-row--depreciated' : ''}>
                                        <td className="as-mva-td">{asset.name}</td>
                                        <td className="as-mva-td">{fmtDateShort(asset.dateOfPurchase)}</td>
                                        <td className="as-mva-td as-mva-num">{fmt(asset.price)}</td>
                                        <td className="as-mva-td as-mva-num">{asset.businessUsePercentage}%</td>
                                        <td className="as-mva-td as-mva-num">
                                            {period ? `${period.years} ${t('screens:fixedAssets.years')}` : '—'}
                                        </td>
                                        <td className="as-mva-td as-mva-num">{fmt(asset.depreciationPerYear)}</td>
                                        <td className="as-mva-td as-mva-num as-contra-amount">
                                            ({fmt(asset.depreciationAmount)})
                                        </td>
                                        <td className="as-mva-td as-mva-num as-mva-boekwaarde">
                                            {fmt(asset.remainingValue)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="as-mva-total-row">
                                <td className="as-mva-td" colSpan={2}>{t('screens:annualStatement.mvaTotal')}</td>
                                <td className="as-mva-td as-mva-num">{fmt(totalPrice)}</td>
                                <td className="as-mva-td" />
                                <td className="as-mva-td" />
                                <td className="as-mva-td as-mva-num">{fmt(totalDeprPerYear)}</td>
                                <td className="as-mva-td as-mva-num as-contra-amount">({fmt(totalDeprAmount)})</td>
                                <td className="as-mva-td as-mva-num as-mva-boekwaarde">{fmt(totalRemainingValue)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        );
    };

    // ── Print helpers ─────────────────────────────────────────────────────────

    const PrintCompanyHeader: React.FC = () => (
        <div className="as-print-header">
            {companyInfo?.companyName && (
                <div className="as-print-company">{companyInfo.companyName}</div>
            )}
            <div className="as-print-meta">
                {[
                    companyInfo?.kvkNumber  ? `KVK: ${companyInfo.kvkNumber}`   : null,
                    companyInfo?.btwId      ? `BTW: ${companyInfo.btwId}`        : null,
                    companyInfo?.addressLine1,
                    companyInfo?.city,
                ]
                    .filter(Boolean)
                    .join(' · ')}
            </div>
            <div className="as-print-doc-title">JAARREKENING</div>
            {selectedFiscalYear && (
                <div className="as-print-period">
                    {selectedFiscalYear.name}&nbsp;&nbsp;|&nbsp;&nbsp;
                    {fmtDate(selectedFiscalYear.start)} – {fmtDate(selectedFiscalYear.end)}
                </div>
            )}
        </div>
    );

    const renderPrintGroups = (groups: Map<string, LedgerBalance[]>) =>
        Array.from(groups.entries()).map(([groupName, bals]) => {
            const grossBals  = bals.filter(lb => !lb.isContraAsset);
            const contraBals = bals.filter(lb => lb.isContraAsset);
            const groupTotal = bals.reduce((s, lb) => s + lb.balance, 0);
            const hasContra  = contraBals.length > 0;
            return (
                <React.Fragment key={groupName}>
                    <div className="as-print-group-title">{groupName}</div>
                    {grossBals.map(lb => (
                        <div key={lb.ledger.id} className="as-print-ledger-row">
                            <span className="as-print-ledger-code">{lb.ledger.code}</span>
                            <span className="as-print-ledger-name">{lb.ledger.name}</span>
                            <span className="as-print-ledger-amount">{fmt(lb.balance)}</span>
                        </div>
                    ))}
                    {contraBals.map(lb => (
                        <div key={lb.ledger.id} className="as-print-ledger-row as-print-ledger-row--contra">
                            <span className="as-print-ledger-code">{lb.ledger.code}</span>
                            <span className="as-print-ledger-name as-print-contra-label">
                                {t('screens:annualStatement.afMin')}&nbsp;{lb.ledger.name}
                            </span>
                            <span className="as-print-ledger-amount as-print-contra-amount">
                                ({fmt(Math.abs(lb.balance))})
                            </span>
                        </div>
                    ))}
                    {hasContra && (
                        <div className="as-print-net-book-value-row">
                            <span>{t('screens:annualStatement.boekwaarde')}</span>
                            <span>{fmt(groupTotal)}</span>
                        </div>
                    )}
                    {!hasContra && groups.size > 1 && (
                        <div className="as-print-group-subtotal">
                            <span>{groupName}</span>
                            <span>{fmt(groupTotal)}</span>
                        </div>
                    )}
                </React.Fragment>
            );
        });

    // ── Print pages ───────────────────────────────────────────────────────────

    const balansPageContent = (
        <div className="as-print-page">
            <PrintCompanyHeader />
            <div className="as-print-section-title">
                {t('screens:annualStatement.balans')}
                {selectedFiscalYear && (
                    <span className="as-print-section-period">
                        &nbsp;{t('screens:annualStatement.balansDate', { date: fmtDate(selectedFiscalYear.end) })}
                    </span>
                )}
            </div>
            <div className="as-print-two-col">
                <div className="as-print-col">
                    <div className="as-print-col-header">{t('screens:annualStatement.activa').toUpperCase()}</div>
                    {renderPrintGroups(activaGroups)}
                    <div className="as-print-col-total">
                        <span>{t('screens:annualStatement.totalActiva')}</span>
                        <span>{fmt(totalActiva)}</span>
                    </div>
                </div>
                <div className="as-print-col">
                    <div className="as-print-col-header">{t('screens:annualStatement.passiva').toUpperCase()}</div>
                     {renderPrintGroups(passivaGroups)}
                    {/* Suppress virtual P&L rows for closed years — EV already holds the result. */}
                    {!isYearClosed && Math.abs(priorYearAccumulatedResult) >= 0.01 && (
                        <div className="as-print-result-row">
                            <span>
                                {t('screens:annualStatement.priorYearResults')}
                                &nbsp;({t('screens:annualStatement.eigenVermogen')})
                            </span>
                            <span className={priorYearAccumulatedResult >= 0 ? 'as-print-positive' : 'as-print-negative'}>
                                {priorYearAccumulatedResult < 0
                                    ? `(${fmt(Math.abs(priorYearAccumulatedResult))})`
                                    : fmt(priorYearAccumulatedResult)}
                            </span>
                        </div>
                    )}
                    {!isYearClosed && (
                        <div className="as-print-result-row">
                            <span>
                                {t('screens:annualStatement.resultaatBoekjaar')}
                                &nbsp;({t('screens:annualStatement.eigenVermogen')})
                            </span>
                            <span className={nettoresultaat >= 0 ? 'as-print-positive' : 'as-print-negative'}>
                                {nettoresultaat < 0
                                    ? `(${fmt(Math.abs(nettoresultaat))})`
                                    : fmt(nettoresultaat)}
                            </span>
                        </div>
                    )}
                    <div className="as-print-col-total">
                        <span>{t('screens:annualStatement.totalPassiva')}</span>
                        <span>{fmt(totalPassiva + cumulativeNetResult)}</span>
                    </div>
                </div>
            </div>
        </div>
    );

    const wplPageContent = (
        <div className="as-print-page">
            <PrintCompanyHeader />
            <div className="as-print-section-title">
                {t('screens:annualStatement.winstVerliesrekening')}
                {selectedFiscalYear && (
                    <span className="as-print-section-period">
                        &nbsp;{t('screens:annualStatement.wplPeriod', { period: selectedFiscalYear.name })}
                    </span>
                )}
            </div>
            <div className="as-print-wpl">
                <div className="as-print-wpl-section">
                    <div className="as-print-col-header">{t('screens:annualStatement.opbrengsten').toUpperCase()}</div>
                    {renderPrintGroups(opbrengstenGroups)}
                    <div className="as-print-subtotal">
                        <span>{t('screens:annualStatement.totalOpbrengsten')}</span>
                        <span>{fmt(totalOpbrengsten)}</span>
                    </div>
                </div>
                 <div className="as-print-wpl-section">
                    <div className="as-print-col-header">{t('screens:annualStatement.kosten').toUpperCase()}</div>
                    {renderPrintGroups(kostenGroups)}
                    <div className="as-print-subtotal">
                        <span>{t('screens:annualStatement.totalKosten')}</span>
                        <span>({fmt(totalKosten)})</span>
                    </div>
                </div>
                <div className={`as-print-net-result ${nettoresultaat >= 0 ? 'as-print-net-result--profit' : 'as-print-net-result--loss'}`}>
                    <span>
                        {t('screens:annualStatement.nettoresultaat')}
                        &nbsp;
                        <span className="as-print-net-result-sub">
                            ({nettoresultaat >= 0
                                ? t('screens:annualStatement.profit')
                                : t('screens:annualStatement.loss')})
                        </span>
                    </span>
                    <span>{fmt(Math.abs(nettoresultaat))}</span>
                </div>
            </div>
        </div>
    );

    const vmsPrintPageContent = (
        <div className="as-print-page">
            <PrintCompanyHeader />
            <div className="as-print-section-title">
                {t('screens:annualStatement.vmsTitle')}
                {selectedFiscalYear && (
                    <span className="as-print-section-period">
                        &nbsp;{t('screens:annualStatement.wplPeriod', { period: selectedFiscalYear.name })}
                    </span>
                )}
            </div>
            <div className="as-print-vms">
                {renderVermogensmutatieRows('as-print-vms-row', 'as-print-vms-total')}
            </div>
        </div>
    );

    const mvaPrintPageContent = bookedAssets.length > 0 ? (
        <div className="as-print-page">
            <PrintCompanyHeader />
            <div className="as-print-section-title">
                {t('screens:annualStatement.mvaToelichting')}
                {selectedFiscalYear && (
                    <span className="as-print-section-period">
                        &nbsp;{t('screens:annualStatement.wplPeriod', { period: selectedFiscalYear.name })}
                    </span>
                )}
            </div>
            <table className="as-print-mva-table">
                {/*
                  * Explicit column widths for portrait A4 (≈154 mm usable).
                  * Name gets the most space; numeric columns are constrained.
                  * col widths must sum to 100 %.
                  */}
                <colgroup>
                    <col style={{ width: '22%' }} />{/* name */}
                    <col style={{ width: '11%' }} />{/* date */}
                    <col style={{ width: '14%' }} />{/* aanschafwaarde */}
                    <col style={{ width: '9%'  }} />{/* zakelijk % */}
                    <col style={{ width: '13%' }} />{/* afschr/jr */}
                    <col style={{ width: '15%' }} />{/* cum afschr */}
                    <col style={{ width: '16%' }} />{/* boekwaarde */}
                </colgroup>
                <thead>
                    <tr>
                        <th className="as-print-mva-th">{t('screens:annualStatement.mvaName')}</th>
                        <th className="as-print-mva-th">{t('screens:annualStatement.mvaPrintDate')}</th>
                        <th className="as-print-mva-th as-print-mva-num">{t('screens:annualStatement.mvaPurchasePrice')}</th>
                        <th className="as-print-mva-th as-print-mva-num">{t('screens:annualStatement.mvaPrintBizUse')}</th>
                        <th className="as-print-mva-th as-print-mva-num">{t('screens:annualStatement.mvaPrintDeprYear')}</th>
                        <th className="as-print-mva-th as-print-mva-num">{t('screens:annualStatement.mvaPrintDeprTotal')}</th>
                        <th className="as-print-mva-th as-print-mva-num">{t('screens:annualStatement.mvaBoekwaarde')}</th>
                    </tr>
                </thead>
                <tbody>
                    {bookedAssets.map(asset => {
                        const period = depreciationPeriods.find(p => p.id === asset.depreciationPeriodId);
                        return (
                            <tr key={asset.id}>
                                <td className="as-print-mva-td">{asset.name}</td>
                                <td className="as-print-mva-td">{fmtDateShort(asset.dateOfPurchase)}</td>
                                <td className="as-print-mva-td as-print-mva-num">{fmt(asset.price)}</td>
                                <td className="as-print-mva-td as-print-mva-num">{asset.businessUsePercentage}%</td>
                                <td className="as-print-mva-td as-print-mva-num">
                                    {period ? `${period.years} ${t('screens:fixedAssets.years')}` : '—'}
                                </td>
                                <td className="as-print-mva-td as-print-mva-num">({fmt(asset.depreciationAmount)})</td>
                                <td className="as-print-mva-td as-print-mva-num">{fmt(asset.remainingValue)}</td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr>
                        <td className="as-print-mva-td" colSpan={2}><strong>{t('screens:annualStatement.mvaTotal')}</strong></td>
                        <td className="as-print-mva-td as-print-mva-num">
                            <strong>{fmt(bookedAssets.reduce((s, a) => s + a.price, 0))}</strong>
                        </td>
                        <td className="as-print-mva-td" />
                        <td className="as-print-mva-td as-print-mva-num">
                            <strong>{fmt(bookedAssets.reduce((s, a) => s + a.depreciationPerYear, 0))}</strong>
                        </td>
                        <td className="as-print-mva-td as-print-mva-num">
                            <strong>({fmt(bookedAssets.reduce((s, a) => s + a.depreciationAmount, 0))})</strong>
                        </td>
                        <td className="as-print-mva-td as-print-mva-num">
                            <strong>{fmt(bookedAssets.reduce((s, a) => s + a.remainingValue, 0))}</strong>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    ) : null;

    const printPages: PrintablePage[] = selectedFiscalYear
        ? [
            { label: t('screens:annualStatement.balansPage'), content: balansPageContent },
            { label: t('screens:annualStatement.wplPage'),    content: wplPageContent    },
            { label: t('screens:annualStatement.vmsPage'),    content: vmsPrintPageContent },
            ...(mvaPrintPageContent
                ? [{ label: t('screens:annualStatement.mvaPage'), content: mvaPrintPageContent }]
                : []),
          ]
        : [];

    // ── Toolbar ───────────────────────────────────────────────────────────────

    const filters: ToolbarFilterConfig[] = [
        {
            key: 'fiscal-year',
            type: 'custom',
            label: t('common:fiscalYear'),
            render: () => (
                <select
                    className="toolbar__filter-select"
                    value={selectedFiscalYearId}
                    onChange={e => setSelectedFiscalYearId(e.target.value)}
                    disabled={!initialized}
                >
                    {!initialized
                        ? <option value="">{t('common:loading')}</option>
                        : fiscalYears.map(fy => (
                            <option key={fy.id} value={fy.id}>{fy.name}</option>
                          ))
                    }
                </select>
            ),
        } as ToolbarFilterConfig,
    ];

    const buttons: ToolbarButtonConfig[] = [
        {
            key: 'print',
            icon: <FaPrint />,
            label: t('screens:annualStatement.printPreview'),
            variant: 'primary',
            onClick: () => setShowPrint(true),
            disabled: !hasData || loading,
        },
    ];

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="annual-statement-page">
            <MainScreenHeader
                title={t('screens:annualStatement.title')}
                subtitle={selectedFiscalYear
                    ? `${selectedFiscalYear.name} · ${fmtDate(selectedFiscalYear.start)} – ${fmtDate(selectedFiscalYear.end)}`
                    : undefined}
            />

            <Toolbar filters={filters} buttons={buttons} />

            {error && <ErrorMessage message={error} />}

            <StatsBar
                items={[
                    {
                        key: 'activa',
                        label: t('screens:annualStatement.activa'),
                        value: fmtEuro(totalActiva),
                    },
                    {
                        key: 'passiva',
                        label: t('screens:annualStatement.passiva'),
                        value: fmtEuro(totalPassiva + cumulativeNetResult),
                    },
                    {
                        key: 'opbrengsten',
                        label: t('screens:annualStatement.opbrengsten'),
                        value: fmtEuro(totalOpbrengsten),
                        variant: 'positive',
                    },
                    {
                        key: 'kosten',
                        label: t('screens:annualStatement.kosten'),
                        value: fmtEuro(totalKosten),
                        variant: 'negative',
                    },
                    {
                        key: 'resultaat',
                        label: t('screens:annualStatement.nettoresultaat'),
                        value: fmtEuro(nettoresultaat),
                        variant: nettoresultaat >= 0 ? 'positive' : 'negative',
                    },
                ]}
            />

            <div className="annual-statement-scroll">
                {loading && (
                    <div className="annual-statement-loading">
                        {t('screens:annualStatement.loading')}
                    </div>
                )}

                {!loading && !hasData && initialized && (
                    <div className="annual-statement-no-data">
                        {t('screens:annualStatement.noData')}
                    </div>
                )}

                {!loading && hasData && (
                    <div className="annual-statement-container">

                    {/* ── Balance-check warning (Dutch GAAP: Activa = Passiva + Resultaat) ─── */}
                        {!isBalanced && (
                            <div className="as-balance-warning">
                                ⚠ {t('screens:annualStatement.balanceCheckWarning', { diff: fmtEuro(balanceDiff) })}
                            </div>
                        )}

                        {/* ── Balans ───────────────────────────────────────── */}
                        <div className="annual-statement-section">
                            <div className="as-section-header">
                                <span className="as-section-title">
                                    {t('screens:annualStatement.balans')}
                                </span>
                                {selectedFiscalYear && (
                                    <span className="as-section-period">
                                        {t('screens:annualStatement.balansDate', { date: fmtDate(selectedFiscalYear.end) })}
                                    </span>
                                )}
                            </div>
                            <div className="as-balans-grid">
                                <div className="as-balans-col">
                                    <div className="as-col-header">{t('screens:annualStatement.activa')}</div>
                                    {renderGroups(activaGroups)}
                                    <div className="as-col-total">
                                        <span>{t('screens:annualStatement.totalActiva')}</span>
                                        <span>{fmt(totalActiva)}</span>
                                    </div>
                                </div>
                                <div className="as-balans-col">
                                    <div className="as-col-header">{t('screens:annualStatement.passiva')}</div>
                                     {renderGroups(passivaGroups)}
                                    {/* For closed years the net result has already been transferred to
                                        Eigen Vermogen via afsluitboekingen — do NOT show virtual P&L
                                        rows here or the total would double-count the result. */}
                                    {!isYearClosed && Math.abs(priorYearAccumulatedResult) >= 0.01 && (
                                        <div className="as-result-row as-result-row--prior">
                                            <span>
                                                {t('screens:annualStatement.priorYearResults')}
                                                &nbsp;
                                                <span className="as-result-row-sub">
                                                    ({t('screens:annualStatement.eigenVermogen')})
                                                </span>
                                            </span>
                                            <span className={priorYearAccumulatedResult >= 0 ? 'as-positive' : 'as-negative'}>
                                                {priorYearAccumulatedResult < 0
                                                    ? `(${fmt(Math.abs(priorYearAccumulatedResult))})`
                                                    : fmt(priorYearAccumulatedResult)}
                                            </span>
                                        </div>
                                    )}
                                    {!isYearClosed && (
                                        <div className="as-result-row">
                                            <span>
                                                {t('screens:annualStatement.resultaatBoekjaar')}
                                                &nbsp;
                                                <span className="as-result-row-sub">
                                                    ({t('screens:annualStatement.eigenVermogen')})
                                                </span>
                                            </span>
                                            <span className={nettoresultaat >= 0 ? 'as-positive' : 'as-negative'}>
                                                {nettoresultaat < 0
                                                    ? `(${fmt(Math.abs(nettoresultaat))})`
                                                    : fmt(nettoresultaat)}
                                            </span>
                                        </div>
                                    )}
                                    <div className="as-col-total">
                                        <span>{t('screens:annualStatement.totalPassiva')}</span>
                                        <span>{fmt(totalPassiva + cumulativeNetResult)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Winst- en Verliesrekening ─────────────────────── */}
                        <div className="annual-statement-section">
                            <div className="as-section-header">
                                <span className="as-section-title">
                                    {t('screens:annualStatement.winstVerliesrekening')}
                                </span>
                                {selectedFiscalYear && (
                                    <span className="as-section-period">
                                        {t('screens:annualStatement.wplPeriod', { period: selectedFiscalYear.name })}
                                    </span>
                                )}
                            </div>
                            {/* Vertical single-column layout per Dutch GAAP (BW2 / RJ 270 categoriale methode) */}
                            <div className="as-wpl-vertical">
                                <div className="as-wpl-section">
                                    <div className="as-col-header">{t('screens:annualStatement.opbrengsten')}</div>
                                    {renderGroups(opbrengstenGroups)}
                                    <div className="as-col-total">
                                        <span>{t('screens:annualStatement.totalOpbrengsten')}</span>
                                        <span>{fmt(totalOpbrengsten)}</span>
                                    </div>
                                </div>
                                <div className="as-wpl-section">
                                    <div className="as-col-header">{t('screens:annualStatement.kosten')}</div>
                                    {renderGroups(kostenGroups)}
                                    <div className="as-col-total">
                                        <span>{t('screens:annualStatement.totalKosten')}</span>
                                        <span>({fmt(totalKosten)})</span>
                                    </div>
                                </div>
                            </div>
                            <div className={`as-net-result ${nettoresultaat >= 0 ? 'as-net-result--profit' : 'as-net-result--loss'}`}>
                                <span className="as-net-result-label">
                                    {t('screens:annualStatement.nettoresultaat')}
                                    &nbsp;
                                    <span className="as-net-result-type">
                                        ({nettoresultaat >= 0
                                            ? t('screens:annualStatement.profit')
                                            : t('screens:annualStatement.loss')})
                                    </span>
                                </span>
                                <span className="as-net-result-amount">
                                    {fmtEuro(Math.abs(nettoresultaat))}
                                </span>
                            </div>
                        </div>

                        {/* ── Vermogensmutatiestaat ─────────────────────────── */}
                        <div className="annual-statement-section">
                            <div className="as-section-header">
                                <span className="as-section-title">
                                    {t('screens:annualStatement.vmsTitle')}
                                </span>
                                {selectedFiscalYear && (
                                    <span className="as-section-period">
                                        {t('screens:annualStatement.wplPeriod', { period: selectedFiscalYear.name })}
                                    </span>
                                )}
                            </div>
                            <div className="as-vms">
                                {renderVermogensmutatieRows('as-vms-row', 'as-vms-total')}
                            </div>
                        </div>

                        {/* ── Toelichting Materiële Vaste Activa ───────────── */}
                        {renderMvaToelichting()}

                    </div>
                )}
            </div>

            <PrintableView
                open={showPrint}
                title={t('screens:annualStatement.printTitle', {
                    year: selectedFiscalYear ? new Date(selectedFiscalYear.start).getFullYear() : '',
                })}
                onClose={() => setShowPrint(false)}
                pages={printPages}
                resetKey={selectedFiscalYearId}
            />
        </div>
    );
};

export default AnnualStatement;


